/**
 * SystemsPage — /systems spoke route.
 * DRAFT content — pending Sakib review.
 * Category-defining page: explains "AI-native business systems" and routes to three offers.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import Button from '../components/ui/Button.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import {
  systemsHead,
  systemsDefinition,
  threeDoorsIn,
  systemsBody,
  systemsCta,
} from '../content/systems.js';

const systemsServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'AI-Native Business Systems',
  description: 'The category we build in. AI-native business systems are operational systems designed from the ground up to run on AI, not retrofitted to an existing SaaS platform. Three delivery modes: The Foundry (founder cohort), Fully Automated Business Systems (custom build), Team Training (enablement).',
  provider: { '@type': 'Organization', name: "The Artificer's AI" },
  areaServed: { '@type': 'Country', name: 'Australia' },
  serviceType: 'AI systems consulting',
};

const systemsBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://artificersai.com/' },
    { '@type': 'ListItem', position: 2, name: 'AI-Native Business Systems', item: 'https://artificersai.com/systems' },
  ],
};

export default function SystemsPage() {
  return (
    <>
      <Helmet>
        <title>AI-Native Business Systems — What the Category Means | The Artificer's AI</title>
        <meta name="description" content="What AI-native business systems are, why they matter, and three ways we deliver them." />
        <link rel="canonical" href="https://artificersai.com/systems" />
        <meta property="og:title" content="AI-Native Business Systems | The Artificer's AI" />
        <meta property="og:description" content="What AI-native business systems are, why they matter, and three ways we deliver them." />
        <meta property="og:url" content="https://artificersai.com/systems" />
      </Helmet>

      {/* Hero + Definition */}
      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <Reveal as="div" delay={0} className="mb-4"><Eyebrow>{systemsHead.eyebrow}</Eyebrow></Reveal>
          <Reveal as="h1" delay={80} className="text-display-lg font-display font-semibold mb-4">{systemsHead.heading}</Reveal>
          <Reveal as="p" delay={140} className="text-body-lg text-ink-200 mb-8">{systemsHead.subheading}</Reveal>
          <Reveal as="h2" delay={0} className="text-h2 font-display font-semibold mb-6">{systemsDefinition.heading}</Reveal>
          <Reveal>
            {systemsDefinition.paragraphs.map((p, i) => (
              <p key={i} className="text-body text-ink-200 mb-4">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Three-doors-in (P0-9: ABOVE THE FOLD) */}
      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-8">{threeDoorsIn.heading}</Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5">
            {threeDoorsIn.doors.map((door, i) => (
              <Reveal key={door.href} delay={i * 80}>
                <Link
                  to={door.href}
                  className="h-full rounded-lg bg-white/[.025] border border-white/[.08] p-6 flex flex-col transition-colors hover:bg-white/[.04] hover:border-white/[.12] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-bright"
                >
                  <h3 className="text-h3 font-display font-semibold text-ink-50 mb-1">{door.title}</h3>
                  <span className="font-mono text-mono-xs text-brand-blue-bright uppercase tracking-wider mb-3">{door.subtitle}</span>
                  <p className="text-body-sm text-ink-200 flex-1 mb-4">{door.description}</p>
                  <span className="font-mono text-mono-xs text-brand-blue-bright">{door.cta}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why this category exists */}
      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{systemsBody.heading}</Reveal>
          <Reveal>
            {systemsBody.paragraphs.map((p, i) => (
              <p key={i} className="text-body text-ink-200 mb-4">{p}</p>
            ))}
          </Reveal>
          <Reveal delay={80} className="mt-8">
            <Button as={Link} to="/intake">{systemsCta.label}</Button>
          </Reveal>
        </div>
      </section>

      <SchemaJsonLd data={systemsServiceSchema} />
      <SchemaJsonLd data={systemsBreadcrumb} />
    </>
  );
}
